import { createSlice } from '@reduxjs/toolkit';
import {
  fetchAssignees,
  createAssigneeThunk,
  updateAssigneeThunk,
  deleteAssigneeThunk,
  fetchCRMAssigneesThunk,
  createCRMAssigneeThunk,
  updateCRMAssigneeThunk,
  deleteCRMAssigneeThunk,
  fetchTagsThunk,
  saveTagsThunk,
  saveLeadTagsThunk,
  fetchUnreadCountsThunk,
  fetchPipelineStatsThunk,
  fetchBlacklistThunk,
  addToBlacklistThunk,
  removeFromBlacklistThunk,
  fetchCognitoGroupsThunk,
  fetchMergedAssigneesThunk,
} from '../thunks/crmThunks';

const initialState = {
  // Assignees (shared across pages — same `assignees` collection used by Sunridge-CRM)
  assignees: [],
  assigneesLoading: false,
  assigneesError: null,
  assigneeSaving: false,
  assigneeSaveError: null,

  // CRM-only assignees (separate crmassignees collection)
  crmAssignees: [],
  crmAssigneesLoading: false,
  crmAssigneesError: null,
  crmAssigneeSaving: false,
  crmAssigneeSaveError: null,

  // Tags
  tags: { leadTags: [], emailTags: [], mobileTags: [] },
  tagsLoading: false,
  tagsError: null,

  // Unread counts
  unreadCounts: { total: 0, byLead: {} },
  unreadCountsLoading: false,

  // Pipeline stats
  pipelineStats: { new: 0, contacted: 0, callback: 0, qualified: 0, appointmentSet: 0, onboarding: 0, converted: 0, lost: 0 },
  pipelineStatsLoading: false,
  pipelineStatsError: null,

  // Blacklist
  blacklist: [],
  blacklistLoading: false,

  // Cognito groups (from main API)
  cognitoGroups: [],
  mergedAssignees: [],
};

const crmSlice = createSlice({
  name: 'crm',
  initialState,
  reducers: {
    setAssignees: (state, action) => {
      state.assignees = action.payload;
    },
    addAssignee: (state, action) => {
      state.assignees.push(action.payload);
    },
    removeAssignee: (state, action) => {
      state.assignees = state.assignees.filter(a => a._id !== action.payload);
    },
    setTags: (state, action) => {
      state.tags = action.payload;
    },
    setLeadTags: (state, action) => {
      state.tags.leadTags = action.payload;
    },
    setEmailTags: (state, action) => {
      state.tags.emailTags = action.payload;
    },
    setMobileTags: (state, action) => {
      state.tags.mobileTags = action.payload;
    },
    setUnreadCounts: (state, action) => {
      state.unreadCounts = action.payload;
    },
    decrementUnread: (state, action) => {
      const leadId = action.payload;
      if (state.unreadCounts.byLead[leadId] > 0) {
        state.unreadCounts.byLead[leadId] -= 1;
        state.unreadCounts.total -= 1;
      }
    },
    incrementUnread: (state, action) => {
      const leadId = action.payload;
      state.unreadCounts.byLead[leadId] = (state.unreadCounts.byLead[leadId] || 0) + 1;
      state.unreadCounts.total += 1;
    },
    markLeadRead: (state, action) => {
      const leadId = action.payload;
      const count = state.unreadCounts.byLead[leadId] || 0;
      state.unreadCounts.total -= count;
      state.unreadCounts.byLead[leadId] = 0;
    },
    setPipelineStats: (state, action) => {
      state.pipelineStats = action.payload;
    },
    setBlacklist: (state, action) => {
      state.blacklist = action.payload;
    },
    addToBlacklist: (state, action) => {
      state.blacklist.push(action.payload);
    },
    removeFromBlacklist: (state, action) => {
      state.blacklist = state.blacklist.filter(id => id !== action.payload);
    },
    setCognitoGroups: (state, action) => {
      state.cognitoGroups = action.payload;
    },
    setMergedAssignees: (state, action) => {
      state.mergedAssignees = action.payload;
    },
    resetCRMState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchAssignees
      .addCase(fetchAssignees.pending, (state) => {
        state.assigneesLoading = true;
        state.assigneesError = null;
      })
      .addCase(fetchAssignees.fulfilled, (state, action) => {
        state.assignees = action.payload;
        state.assigneesLoading = false;
      })
      .addCase(fetchAssignees.rejected, (state, action) => {
        state.assigneesLoading = false;
        state.assigneesError = action.error.message;
      })

      // createAssigneeThunk
      .addCase(createAssigneeThunk.pending, (state) => {
        state.assigneeSaving = true;
        state.assigneeSaveError = null;
      })
      .addCase(createAssigneeThunk.fulfilled, (state, action) => {
        // Shared /api/assignees returns the existing doc when the name already
        // exists. Avoid pushing duplicates by id.
        const incoming = action.payload;
        if (incoming && !state.assignees.some((a) => a._id === incoming._id)) {
          state.assignees.push(incoming);
        }
        state.assigneeSaving = false;
      })
      .addCase(createAssigneeThunk.rejected, (state, action) => {
        state.assigneeSaving = false;
        state.assigneeSaveError = action.payload?.message || action.payload || action.error?.message;
      })

      // updateAssigneeThunk
      .addCase(updateAssigneeThunk.pending, (state) => {
        state.assigneeSaving = true;
        state.assigneeSaveError = null;
      })
      .addCase(updateAssigneeThunk.fulfilled, (state, action) => {
        const idx = state.assignees.findIndex((a) => a._id === action.payload._id);
        if (idx !== -1) state.assignees[idx] = action.payload;
        state.assigneeSaving = false;
      })
      .addCase(updateAssigneeThunk.rejected, (state, action) => {
        state.assigneeSaving = false;
        state.assigneeSaveError = action.payload?.message || action.payload || action.error?.message;
      })

      // deleteAssigneeThunk
      .addCase(deleteAssigneeThunk.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.assignees = state.assignees.filter(a => a._id !== id);
      })

      // fetchTagsThunk — flatten the { assigneeTags, leadTags } response so
      // consumers can read tags.emailTags / .mobileTags / .leadTags directly
      .addCase(fetchTagsThunk.pending, (state) => {
        state.tagsLoading = true;
        state.tagsError = null;
      })
      .addCase(fetchTagsThunk.fulfilled, (state, action) => {
        const { assigneeTags, leadTags } = action.payload || {};
        state.tags = {
          emailTags: assigneeTags?.emailTags || [],
          mobileTags: assigneeTags?.mobileTags || [],
          leadTags: leadTags?.leads || leadTags || [],
        };
        state.tagsLoading = false;
      })
      .addCase(fetchTagsThunk.rejected, (state, action) => {
        state.tagsLoading = false;
        state.tagsError = action.error.message;
      })

      // saveTagsThunk — server returns the full tags doc; preserve leadTags
      .addCase(saveTagsThunk.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.tags = {
          emailTags: payload.emailTags || [],
          mobileTags: payload.mobileTags || [],
          leadTags: state.tags.leadTags || [],
        };
      })

      // saveLeadTagsThunk — overwrite leadTags with the just-saved payload
      .addCase(saveLeadTagsThunk.fulfilled, (state, action) => {
        state.tags.leadTags = action.payload?.leadTags || [];
      })

      // fetchCRMAssigneesThunk
      .addCase(fetchCRMAssigneesThunk.pending, (state) => {
        state.crmAssigneesLoading = true;
        state.crmAssigneesError = null;
      })
      .addCase(fetchCRMAssigneesThunk.fulfilled, (state, action) => {
        state.crmAssignees = Array.isArray(action.payload) ? action.payload : [];
        state.crmAssigneesLoading = false;
      })
      .addCase(fetchCRMAssigneesThunk.rejected, (state, action) => {
        state.crmAssigneesLoading = false;
        state.crmAssigneesError = action.payload || action.error?.message;
      })

      // createCRMAssigneeThunk
      .addCase(createCRMAssigneeThunk.pending, (state) => {
        state.crmAssigneeSaving = true;
        state.crmAssigneeSaveError = null;
      })
      .addCase(createCRMAssigneeThunk.fulfilled, (state, action) => {
        state.crmAssignees.push(action.payload);
        state.crmAssignees.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        state.crmAssigneeSaving = false;
      })
      .addCase(createCRMAssigneeThunk.rejected, (state, action) => {
        state.crmAssigneeSaving = false;
        state.crmAssigneeSaveError = action.payload?.message || action.payload || action.error?.message;
      })

      // updateCRMAssigneeThunk
      .addCase(updateCRMAssigneeThunk.pending, (state) => {
        state.crmAssigneeSaving = true;
        state.crmAssigneeSaveError = null;
      })
      .addCase(updateCRMAssigneeThunk.fulfilled, (state, action) => {
        const idx = state.crmAssignees.findIndex(a => a._id === action.payload._id);
        if (idx !== -1) state.crmAssignees[idx] = action.payload;
        state.crmAssigneeSaving = false;
      })
      .addCase(updateCRMAssigneeThunk.rejected, (state, action) => {
        state.crmAssigneeSaving = false;
        state.crmAssigneeSaveError = action.payload?.message || action.payload || action.error?.message;
      })

      // deleteCRMAssigneeThunk
      .addCase(deleteCRMAssigneeThunk.fulfilled, (state, action) => {
        state.crmAssignees = state.crmAssignees.filter(a => a._id !== action.payload);
      })

      // fetchUnreadCountsThunk
      .addCase(fetchUnreadCountsThunk.pending, (state) => {
        state.unreadCountsLoading = true;
      })
      .addCase(fetchUnreadCountsThunk.fulfilled, (state, action) => {
        state.unreadCounts = action.payload;
        state.unreadCountsLoading = false;
      })
      .addCase(fetchUnreadCountsThunk.rejected, (state) => {
        state.unreadCountsLoading = false;
      })

      // fetchPipelineStatsThunk
      .addCase(fetchPipelineStatsThunk.pending, (state) => {
        state.pipelineStatsLoading = true;
        state.pipelineStatsError = null;
      })
      .addCase(fetchPipelineStatsThunk.fulfilled, (state, action) => {
        state.pipelineStats = action.payload;
        state.pipelineStatsLoading = false;
      })
      .addCase(fetchPipelineStatsThunk.rejected, (state, action) => {
        state.pipelineStatsLoading = false;
        state.pipelineStatsError = action.error.message;
      })

      // fetchBlacklistThunk
      .addCase(fetchBlacklistThunk.fulfilled, (state, action) => {
        state.blacklist = action.payload;
      })

      // addToBlacklistThunk
      .addCase(addToBlacklistThunk.fulfilled, (state, action) => {
        state.blacklist.push(action.payload);
      })

      // removeFromBlacklistThunk
      .addCase(removeFromBlacklistThunk.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.blacklist = state.blacklist.filter(item => item !== id);
      })

      // fetchCognitoGroupsThunk
      .addCase(fetchCognitoGroupsThunk.fulfilled, (state, action) => {
        state.cognitoGroups = action.payload;
      })

      // fetchMergedAssigneesThunk
      .addCase(fetchMergedAssigneesThunk.fulfilled, (state, action) => {
        state.mergedAssignees = action.payload;
      });
  },
});

export const {
  setAssignees,
  addAssignee,
  removeAssignee,
  setTags,
  setLeadTags,
  setEmailTags,
  setMobileTags,
  setUnreadCounts,
  decrementUnread,
  incrementUnread,
  markLeadRead,
  setPipelineStats,
  setBlacklist,
  addToBlacklist,
  removeFromBlacklist,
  setCognitoGroups,
  setMergedAssignees,
  resetCRMState,
} = crmSlice.actions;

export default crmSlice.reducer;
